// Бланки актов проверок радио/навигации — КОПИЯ статических шаблонов портала 73.
//
// ЗАЧЕМ ЭТОТ ФАЙЛ ЕСТЬ. В зеркале ядра у акта лежат только ЗНАЧЕНИЯ (`data.rows`,
// `data.header`, `data.checks`, `data.items`) и код шаблона (`type`). Сам бланк — русские и
// английские формулировки пунктов, вступление, заключение, состав таблиц, число колонок под
// приборы — живёт в исходнике портала (`73-portal/src/acts_templates.js`) и в зеркало не
// переносится вовсе. Без него напечатать акт нельзя: получится таблица «hexid → 2C8B…»,
// которую на судне никто не примет.
//
// ЭТО КОПИЯ, А НЕ ИСТОЧНИК ПРАВДЫ. Портал 73 мы не меняем (прямое требование владельца), но
// он меняется сам. Если на портале правят формулировку пункта или добавляют строку в бланк,
// здесь этого не появится. Два следствия, и оба видны на листе:
//   • строка, которой нет в этой копии, но есть в данных, печатается СВОИМ КОДОМ и попадает
//     в перечень «строки бланка, которых копия не знает» (см. lib/print73.ts);
//   • на самом листе стоит служебная отметка с датой копии — чтобы человек, у которого лист
//     разошёлся с порталом, знал, где искать причину.
//
// Списано 28.07.2026 из `73-portal/src/acts_templates.js` (шаблоны СиР v3.8, формы
// Е. Марченко), построчно, без правок формулировок — включая опечатки оригинала
// («Проверки места установки», «емкость» без ё). Менять текст здесь нельзя: расхождение
// с бумагой портала опаснее опечатки.

/** Строка бланка: код в данных, русская и английская подпись, значение по умолчанию. */
export interface СтрокаБланка {
  k: string
  ru: string
  en: string
  /** Что портал подставляет в пустое поле при СОЗДАНИИ акта. На печати не используется:
   *  подставлять «Checked, OK» за инженера — значит выдумать результат проверки. */
  def?: string
  /** Значение хранится массивом — по одному на прибор (три носимые УКВ, два РЛО). */
  per_unit?: boolean
  /** Поле-срок: в бланке подсказка «ММ.ГГГГ». Нужно, чтобы отличить дату от текста. */
  date_hint?: boolean
  /** Переключатель «Годное / Не годное». */
  gng?: boolean
}

export interface РазделБланка {
  title: string
  rows: СтрокаБланка[]
}

export interface Бланк {
  key: string
  /** Суффикс в номере акта: `<IMO>-<ММГГГГ>-<suffix>`. */
  suffix: string
  units_max: number
  units_label?: string
  name: string
  name_en: string
  intro?: string
  intro_en?: string
  note?: string
  outro?: string
  outro_en?: string
  footer_next?: string
  header: СтрокаБланка[]
  rows?: СтрокаБланка[]
  sections?: РазделБланка[]
  extra?: СтрокаБланка[]
  /** Чек-лист бланка: тексты пунктов, двуязычные через « / ». Есть только у АРБ. */
  checks?: string[]
}

/** Дата, когда копия снята с портала. Печатается на листе служебной отметкой. */
export const БЛАНКИ_СНЯТЫ = '28.07.2026'

const ШАПКА_СУДНА: СтрокаБланка[] = [
  { k: 'vessel', ru: 'Название судна', en: 'Name of ship' },
  { k: 'imo', ru: 'Номер ИМО', en: 'IMO number' },
  { k: 'mmsi', ru: 'Идентификационный номер (MMSI)', en: 'Identification number (MMSI)' },
]

const СЛЕДУЮЩЕЕ = 'Дата следующего освидетельствования: в пределах трех месяцев до или трех месяцев после даты, отсчитываемой от назначенной даты последнего освидетельствования.'

/** Реквизиты в шапке бумажного бланка. Взяты из `actPrint` (73-portal/public/app.js). */
export const ФИРМА = {
  название: 'Связь и Радионавигация',
  полное: 'ООО «Связь и Радионавигация СПб»',
  полное_en: 'Communication & Radionavigation SPb Co ltd',
  телефон: '+7 (812) 679-09-10',
  почта: 'service@cirspb.ru',
  сайт: 'www.cirspb.ru',
  адрес: '198035, Санкт-Петербург,<br>ул. Виндавская, 2Д',
  адрес_en: '198035, St. Petersburg,<br>Vindavskaya str., 2D',
  сертификаты: 'Работы произведены в соответствии с требованиями для класса судна, сертификаты опубликованы на сайте https://cirspb.ru',
  сертификаты_en: "Service have been carried out in accordance with the requirements of class vessel, certificates are published on website https://cirspb.ru",
} as const

export const БЛАНКИ: Бланк[] = [
  {
    key: 'vhf', suffix: 'VHF', units_max: 3, units_label: 'УКВ-радиостанций',
    name: 'Акт ежегодной комплексной проверки ОВЧ-радиостанций двусторонней связи',
    name_en: 'Certificate of annual test of VHF-two-way communication radio stations',
    intro: 'Настоящий акт составлен о том, что представителем ООО "Связь и Радионавигация СПб" проведена комплексная проверка носимой ОВЧ-радиостанции и установлено следующее:',
    intro_en: 'Present act is established that representative from Communication & Radionavigation SPb Co. Ltd performed testing of handled VHF radiostation and defined the following:',
    outro: 'В результате установлено, что механические, электрические параметры проверяемой ОВЧ-радиостанции, соответствуют требованиям Службы Морского флота Минтранса и Регистра судна.',
    outro_en: 'It is hereby certified that all mechanical, electrical parameters VHF radiostation complies with the requirements of Russian Maritime Administration and Register of ship.',
    footer_next: СЛЕДУЮЩЕЕ,
    header: ШАПКА_СУДНА,
    rows: [
      { k: 'model', ru: 'Модель, завод-изготовитель', en: 'VHF model, manufacturer name', def: '' },
      { k: 'serial', ru: 'Серийный номер', en: 'Serial number', def: '', per_unit: true },
      { k: 'power', ru: 'Мощность несущей передатчика на всех каналах (Вт)', en: 'Transmit power on channels (Wt)', def: 'Hi –    / Low –', per_unit: true },
      { k: 'housing', ru: 'Проверка целостности корпуса', en: 'Checking the integrity of the housing', def: 'Checked, OK' },
      { k: 'place', ru: 'Проверки места установки и наличия символа ИМО', en: 'Checking the installation location and the presence of the IMO symbol', def: 'Checked, OK' },
      { k: 'battery', ru: 'Срок годности элементов питания', en: 'Battery expiry date', def: '', per_unit: true, date_hint: true },
    ],
  },
  {
    key: 'sart', suffix: 'SART', units_max: 2, units_label: 'РЛО',
    name: 'Акт ежегодной комплексной проверки РЛО',
    name_en: 'Certificate of annual test of SART',
    intro: 'Настоящий акт составлен о том, что представителем ООО "Связь и Радионавигация СПб" проведена комплексная проверка радиолокационного ответчика, и установлено следующее:',
    intro_en: 'Present act is established that representative from Communication & Radionavigation SPb Co. Ltd performed testing of SART and defined the following:',
    outro: 'В результате установлено, что механические, электрические проверяемого РЛО, соответствуют требованиям Службы Морского флота Минтранса и Регистра судна.',
    outro_en: 'It is hereby certified that all mechanical, electrical parameters SART complies with the requirements of Russian Maritime Administration and Register of ship.',
    footer_next: СЛЕДУЮЩЕЕ,
    header: ШАПКА_СУДНА,
    rows: [
      { k: 'model', ru: 'Модель РЛО, завод-изготовитель', en: 'SART model, manufacturer name', def: '' },
      { k: 'serial', ru: 'Серийный номер', en: 'Serial number', def: '', per_unit: true },
      { k: 'marking', ru: 'Наличие и качество маркировки на корпусе', en: 'Availability and quality of markings', def: 'Checked, OK' },
      { k: 'radar', ru: 'Наличие меток на экране РЛС', en: 'Indication on Radar display', def: 'Checked, OK' },
      { k: 'battery', ru: 'Срок годности элементов питания', en: 'Battery expire date', def: '', per_unit: true, date_hint: true },
    ],
  },
  {
    key: 'aissart', suffix: 'AIS-SART', units_max: 2, units_label: 'AIS-SART',
    name: 'Акт ежегодной комплексной проверки передатчика АИС',
    name_en: 'Certificate of annual test of AIS-SART',
    intro: 'Настоящий акт составлен о том, что представителем ООО "Связь и Радионавигация СПб" проведена комплексная проверка передатчика АИС, и установлено следующее:',
    intro_en: 'Present act is established that representative from Communication & Radionavigation SPb Co. Ltd performed testing of AIS-SART and defined the following:',
    note: 'Проверка передатчика АИС производилась тестером AEROMARINE SRT MRTS-7M и судовым УАИС.',
    outro: 'В результате установлено, что механические, электрические проверяемого передатчика АИС, соответствуют требованиям Службы Морского флота Минтранса и Регистра судна.',
    outro_en: 'It is hereby certified that all mechanical, electrical parameters AIS-SART complies with the requirements of Russian Maritime Administration and Register of ship.',
    footer_next: СЛЕДУЮЩЕЕ,
    header: ШАПКА_СУДНА,
    rows: [
      { k: 'model', ru: 'Модель AIS-SART, завод-изготовитель', en: 'AIS-SART model, manufacturer name', def: '' },
      { k: 'serial', ru: 'Серийный номер', en: 'Serial number', def: '', per_unit: true },
      { k: 'id', ru: 'Заводской идентификатор ID (MMSI)', en: 'Manufacturer serialized ID (MMSI)', def: '', per_unit: true },
      { k: 'marking', ru: 'Наличие и качество маркировки на корпусе', en: 'Availability and quality of markings', def: 'Checked, OK' },
      { k: 'target', ru: 'Отображение цели AIS-SART', en: 'AIS-SART target indication', def: 'Checked, OK' },
      { k: 'battery', ru: 'Срок годности элементов питания', en: 'Battery expire date', def: '', per_unit: true, date_hint: true },
    ],
  },
  {
    key: 'batt', suffix: 'BATT', units_max: 1,
    name: 'Акт ежегодной комплексной проверки аккумуляторной батареи ГМССБ',
    name_en: 'Certificate of annual test of GMDSS battery',
    intro: 'Настоящий акт составлен о том, что представителем ООО "Связь и Радионавигация СПб" проведена проверка аккумуляторной батареи ГМССБ, и установлено следующее:',
    intro_en: 'Present act is established that representative from Communication & Radionavigation SPb Co. Ltd performed testing of battery GMDSS and defined the following:',
    note: 'Проверка батареи производилась в судовых условиях. На судне ____ аварийный дизель генератор.',
    outro: 'В результате установлено, что электрические параметры соответствуют требованиям Службы Морского флота Минтранса и Регистра судна.',
    outro_en: 'It is hereby certified that electrical parameters complies with the requirements of Russian Maritime Administration and Register of ship.',
    footer_next: СЛЕДУЮЩЕЕ,
    header: ШАПКА_СУДНА,
    rows: [
      { k: 'model', ru: 'Модель батареи (завод-изготовитель)', en: 'Battery model (manufacturing name)', def: '' },
      { k: 'year', ru: 'Год установки АКБ', en: 'Year of batteries installation', def: '' },
      { k: 'voltage', ru: 'Рабочее напряжение, В', en: 'Voltage, V', def: '' },
      { k: 'hours', ru: 'Время работы оборудования ГМССБ (час)', en: 'Time work equipment GMDSS (hour)', def: '' },
      { k: 'rated', ru: 'Номинальная емкость аккумулятора (А/ч)', en: 'Rated capacity of the battery (A/h)', def: '' },
      { k: 'actual', ru: 'Остаточная емкость аккумулятора (А/ч)', en: 'Capacity of battery (A/h)', def: '' },
      { k: 'remain', ru: 'Остаточная емкость батарей составляет, %', en: 'The remaining capacity of the battery is, %', def: '' },
    ],
  },
  {
    key: 'epirb', suffix: 'EPIRB', units_max: 1,
    name: 'Акт ежегодной комплексной проверки АРБ 406 МГц',
    name_en: 'Certificate of annual test of 406 MHz EPIRBs',
    intro: 'В соответствии с требованиями ИМО MSC.1/Circ.1040/Rev.2 и СОЛАС Рег. IV/15,9 проведена ежегодная комплексная проверка аварийного радиобуя 406 МГц и установлено следующее:',
    intro_en: 'In accordance with the requirements of IMO MSC.1/Circ.1040/Rev.2 and SOLAS Reg. IV/15,9 performed testing of 406 MHz EPIRB and defined the following:',
    outro: 'It is hereby certified that all mechanical, electrical and information parameters, as well as the documentation on the 406 MHz EPIRB comply with specification and the requirements of Russian Maritime Administration and Register of ship. The identification number of the tested EPIRB complies with the EPIRB registration number in Mission Control Center (MCC). The volume of examinations required by this form meets IMO Circular MSC/Circ.1040 Rev.2',
    footer_next: СЛЕДУЮЩЕЕ,
    header: ШАПКА_СУДНА,
    rows: [
      { k: 'model', ru: 'Модель АРБ, завод-изготовитель', en: 'EPIRB model, manufacturer name', def: '' },
      { k: 'serial', ru: 'Заводской номер АРБ', en: 'EPIRB serial number', def: '' },
      { k: 'mcc', ru: 'Наличие документа, подтверждающего регистрацию в МКВЦ', en: 'Availability of MCC registration', def: 'Годное / Good', gng: true },
      { k: 'protocol', ru: 'Тип протокола', en: 'Protocol type', def: '' },
      { k: 'country', ru: 'Код страны', en: 'Country code', def: '' },
      { k: 'hexid', ru: 'Идентификационный номер (содержание посылки с 26 по 85 бит)', en: 'Beacon 15 Hex ID (message contents from 26 to 85 bits)', def: '' },
      { k: 'msg112', ru: 'Содержание посылки с 1 по 112 бит', en: 'Message contents from 1 to 112 bits', def: '' },
      { k: 'aisid', ru: 'Идентификатор передатчика АИС (если применимо)', en: 'AIS Transmitter ID (if applicable)', def: 'not applicable' },
      { k: 'bch', ru: 'BCH', en: 'BCH', def: '' },
      { k: 'f1215', ru: 'Наличие несущей частоты передатчика 121,5 МГц', en: 'Availability of 121.5 MHz', def: 'Годное / Good', gng: true },
      { k: 'f406', ru: 'Измерение несущей частоты 406 МГц', en: 'Measurement of the carrier frequency 406 MHz, MHz', def: '' },
      { k: 'strobe', ru: 'Состояние проблескового маяка', en: 'Strobe light condition', def: 'Годное / Good', gng: true },
      { k: 'hru', ru: 'Тип и срок годности устройства автоматического отделения', en: 'Type and expiry date of automatic release mechanism', def: '', date_hint: true },
      { k: 'battery_next', ru: 'Дата следующей замены элементов питания', en: 'Next battery replacement date', def: '', date_hint: true },
      { k: 'sbm_next', ru: 'Дата очередного берегового обслуживания', en: 'Next date of shore-based maintenance', def: '', date_hint: true },
      { k: 'tester', ru: 'Тип и сер. номер измерительного оборудования', en: 'Type and ser. number of measuring equipment', def: 'Tron Unidec s/n: 201BE02258, GMDSS Tester MRTS-7M' },
    ],
    checks: [
      'АРБ расположен и закреплен в месте, обеспечивающем свободное всплытие / EPIRB position and mounting for float-free operation',
      'Визуальный осмотр АРБ и бракеты на предмет дефектов, повреждения, или трещин в корпусе / Visual inspection of the EPIRB and bracket',
      'Самопроверка АРБ, включая самопроверку ГНСС, если применимо / EPIRB self-test routine, incl. GNSS self-test',
      'Идентификационный номер EPIRB четко обозначен на внешней стороне / EPIRB identification is clearly marked outside',
      'Идентификатор EPIRB идентичен информации, нанесенной на АРБ / Decoded hex ID is identical to the marked one',
      'Номер MMSI или позывной соответствует присвоенному судну / MMSI or call sign corresponds to the ship',
      'Декодированный децимальный номер (MMSI) соответствует судовому MMSI / Decoded decimal (MMSI) corresponds to ship MMSI',
      'Проверка регистрации в соответствующей базе данных / Verifying registration in beacon database',
      'Батарея имеет не истекший срок годности / The battery has not expired',
      'Устройство автоматического отделения имеет не истекший срок годности / The automatic release device has not expired',
      'Проверка излучения в полосе 406 МГц / Verifying the emission in the 406 MHz band',
      'Проверка излучения на частоте 121,5 МГц, если применимо / Verifying emission on 121.5 MHz, if applicable',
      'Проверка излучения на частотах АИС, если применимо / Verifying emission on AIS frequencies, if applicable',
      'АРБ обслуживается с интервалами, требуемыми Администрацией (MSC/Circ.1039) / EPIRB has SBM at required intervals',
      'Линь в хорошем состоянии и надежно закреплен / Firmly attached lanyard in good condition',
      'После испытаний АРБ на штатном месте, ложные сигналы отсутствуют / After test EPIRB back in place, no false signals',
      'Руководство по эксплуатации АРБ имеется на борту / Operating Manual EPIRB available on board',
    ],
  },
  {
    key: 'sbm_epirb', suffix: 'SBM EP', units_max: 1,
    name: 'Акт Берегового Технического Обслуживания АРБ 406 МГц',
    name_en: 'Certificate of Shore-based Maintenance of 406 MHz EPIRB',
    intro: 'Настоящий акт составлен о том, что представителем ООО «Связь и Радионавигация СПб» проведено береговое техническое обслуживание аварийного радиобуя 406 МГц, и установлено следующее:',
    intro_en: 'Present act is established that the representative from Communication & Radionavigation SPb Co Ltd performed the shore-based maintenance of 406 MHz EPIRB and defined the following:',
    outro: 'В результате установлено, что механические, электрические, светотехнические и информационные параметры, водонепроницаемость, а также документация на проверяемый АРБ 406 соответствуют паспортным данным, требованиям ИМО MSC.1/цирк.1040/SOLAS IV/15.9, Морской Администрации флага и Регистра судна. Идентификационный номер проверяемого АРБ 406 соответствует регистрационному номеру МКВЦ системы КОСПАС-САРСАТ. Объем проверок соответствует циркуляру ИМО MSC/Circ.1039.',
    outro_en: 'It is hereby certified that all parameters and documentation comply with specification and requirements of IMO MSC.1/Circ.1040/SOLAS IV/15.9. The volume of examinations meets IMO Circular MSC/Circ.1039.',
    header: ШАПКА_СУДНА,
    rows: [
      { k: 'model', ru: 'Модель АРБ, завод-изготовитель', en: 'EPIRB model, manufacturer name', def: '' },
      { k: 'serial', ru: 'Заводской номер АРБ', en: 'EPIRB serial number', def: '' },
      { k: 'country', ru: 'Код страны', en: 'Country code', def: '' },
      { k: 'hexid', ru: 'Идентификационный номер (содержание посылки с 26 по 85 бит)', en: 'Beacon 15 Hex ID', def: '' },
      { k: 'aisid', ru: 'Идентификатор передатчика АИС (если применимо)', en: 'AIS Transmitter ID (if applicable)', def: '' },
      { k: 'msg112', ru: 'Содержание посылки с 1 по 112 бит', en: 'Message contents from 1 to 112 bits', def: '' },
      { k: 'f406', ru: 'Значение несущей частоты передатчика 406 МГц и уход частоты', en: 'Measured value and vary of 406 MHz transmitter frequency', def: '' },
      { k: 'f1215', ru: 'Наличие передаваемого сигнала 121,5 с качанием тона', en: 'Availability of transmitted 121.5 MHz signal with swept tone', def: 'Годное / Good', gng: true },
      { k: 'power', ru: 'Выходная мощность передатчика, W', en: 'Output transmitting power, W', def: '' },
      { k: 'waterproof', ru: 'Водонепроницаемость', en: 'Waterproof Integrity', def: 'Годное / Good', gng: true },
      { k: 'strobe', ru: 'Состояние проблескового маяка', en: 'Strobe light condition', def: 'Годное / Good', gng: true },
      { k: 'hru', ru: 'Наличие и срок годности устройства автоматического отделения', en: 'Availability and expiry date of automatic release mechanism', def: '', date_hint: true },
      { k: 'marking', ru: 'Наличие и качество маркировки на корпусе', en: 'Availability and quality of markings', def: 'Годное / Good', gng: true },
      { k: 'battery_next', ru: 'Дата следующей замены элементов питания', en: 'Next battery replacement date', def: '', date_hint: true },
      { k: 'sbm_next', ru: 'Дата очередного БТО', en: 'Date of next shore-based maintenance', def: '', date_hint: true },
    ],
  },
  {
    key: 'ais', suffix: 'AIS', units_max: 1,
    name: 'Акт ежегодной комплексной проверки оборудования УАИС',
    name_en: 'Certificate of annual test of UAIS',
    footer_next: СЛЕДУЮЩЕЕ,
    outro: 'The AIS has been tested according to IMO SN/Circ.227 and resolution MSC.74(69), annex 3.',
    header: [
      { k: 'vessel', ru: 'Название судна', en: 'Name of ship' },
      { k: 'call_sign', ru: 'Позывной', en: 'Call sign' },
      { k: 'mmsi', ru: 'MMSI', en: 'MMSI number' },
      { k: 'port_registry', ru: 'Порт приписки', en: 'Port of registry' },
      { k: 'imo', ru: 'Номер ИМО', en: 'IMO number' },
      { k: 'gross', ru: 'Валовая вместимость', en: 'Gross tonnage' },
      { k: 'keel', ru: 'Дата закладки киля', en: 'Date keel laid' },
    ],
    sections: [
      {
        title: '1. Сведения об установке / Installation details',
        rows: [
          { k: 's1_1', ru: 'Тип аппаратуры АИС (s/n)', en: 'AIS transponder type (s/n)', def: '' },
          { k: 's1_2', ru: 'Свидетельство о типовом одобрении', en: 'Type approval certificate', def: 'Checked, OK' },
          { k: 's1_3', ru: 'Наличие на борту отчета о первоначальной установке', en: 'Initial installation configuration report on board', def: 'Checked, OK' },
          { k: 's1_4', ru: 'Наличие чертежей (блоки, антенны, подключения)', en: 'Drawings provided (antenna, arrangement, block diagram)', def: 'Checked, OK' },
          { k: 's1_5', ru: 'Основной источник электроэнергии', en: 'Main source of electrical power', def: 'Checked, OK' },
          { k: 's1_6', ru: 'Аварийный источник электроэнергии (генератор или АКБ)', en: 'Emergency source of electrical power', def: 'Checked, OK' },
          { k: 's1_7', ru: 'Проверена ёмкость, если АИС подключена к АКБ', en: 'Capacity verified if AIS connected to a battery', def: '' },
          { k: 's1_8', ru: 'Лоцманский разъём вблизи рабочего места лоцмана', en: 'Pilot plug near pilots operating position', def: 'Checked, OK' },
          { k: 's1_9', ru: 'Доступ к 120 В вблизи лоцманского разъёма (Панама/Св. Лаврентий)', en: '120 V AC provided near pilot', def: '' },
        ],
      },
      {
        title: '2. Программирование АИС — статическая информация / Static information',
        rows: [
          { k: 's2_1', ru: 'MMSI', en: 'MMSI number', def: '' },
          { k: 's2_2', ru: 'Номер ИМО', en: 'IMO number', def: '' },
          { k: 's2_3', ru: 'Радиопозывной', en: 'Radio call sign', def: '' },
          { k: 's2_4', ru: 'Название судна', en: 'Name of ship', def: '' },
          { k: 's2_5', ru: 'Тип судна', en: 'Type of ship', def: '' },
          { k: 's2_6', ru: 'Длина/ширина судна, м', en: 'Ship length and beam, m', def: '' },
          { k: 's2_7', ru: 'Расположение GPS антенны (A/B/C/D)', en: 'Location of GPS antenna', def: 'A= , B= , C= , D=' },
        ],
      },
      {
        title: '3. Программирование АИС — динамическая информация / Dynamic information',
        rows: [
          { k: 's3_1', ru: 'Позиция судна с точностью и целостностью', en: 'Ships position with accuracy and integrity', def: 'Checked, OK (Int&Ext GNSS)' },
          { k: 's3_2', ru: 'Время UTC', en: 'Time in UTC', def: 'Checked, OK (Int. GNSS)' },
          { k: 's3_3', ru: 'Курс относительно грунта (COG)', en: 'Course over ground (COG)', def: 'Checked, OK (Int&Ext GNSS)' },
          { k: 's3_4', ru: 'Скорость относительно грунта (SOG)', en: 'Speed over ground (SOG)', def: 'Checked, OK (Int&Ext GNSS)' },
          { k: 's3_5', ru: 'Курс (гирокомпас или прочее)', en: 'Course (Gyro or other)', def: 'Checked, OK' },
          { k: 's3_6', ru: 'Навигационный статус', en: 'Navigation status', def: 'Checked, OK' },
          { k: 's3_7', ru: 'Угловая скорость поворота (ROT)', en: 'Rate of turn (ROT)', def: 'Checked, OK' },
        ],
      },
      {
        title: '4. Программирование АИС — информация о рейсе / Voyage related information',
        rows: [
          { k: 's4_1', ru: 'Осадка судна', en: 'Ships draught', def: 'Checked, OK' },
          { k: 's4_2', ru: 'Тип груза', en: 'Type of cargo', def: 'Checked, OK' },
          { k: 's4_3', ru: 'Пункт назначения и ETA', en: 'Destination and ETA', def: 'Checked, OK' },
          { k: 's4_4', ru: 'План маршрута (опционально)', en: 'Route plan (optional)', def: 'Checked, OK' },
          { k: 's4_5', ru: 'Короткие сообщения безопасности', en: 'Short safety-related messages', def: 'Checked, OK' },
        ],
      },
      {
        title: '5. Тест с измерительным прибором / Performance test using measuring instrument',
        rows: [
          { k: 's5_1', ru: 'Измерение частоты АИС кан. 1, 2, 70 ЦИВ', en: 'Frequency measurements AIS ch. 1/2, GMDSS ch.70', def: 'Checked, OK' },
          { k: 's5_2', ru: 'Измерение мощности АИС кан. 1, 2, 70 ЦИВ', en: 'Power measurements AIS ch.1/2, GMDSS ch.70', def: 'Checked, OK' },
          { k: 's5_3', ru: 'Информация с опросом на 70 канале', en: 'Polling information ch. 70', def: 'Checked, OK' },
          { k: 's5_4', ru: 'Считывание данных с дисплея АИС', en: 'Read data from AIS', def: 'Checked, OK' },
          { k: 's5_5', ru: 'Передача информации в аппаратуру АИС', en: 'Send data to AIS', def: 'Checked, OK' },
          { k: 's5_6', ru: 'Проверка АИС на опрос «виртуальных судов»', en: 'Check AIS response to “virtual vessels”', def: 'Checked, OK' },
        ],
      },
      {
        title: '6. Тест «в эфире» / “On air” performance test',
        rows: [
          { k: 's6_1', ru: 'Проверка функции приёма сигналов', en: 'Check reception performance', def: 'Checked, OK' },
          { k: 's6_2', ru: 'Приём собственного сигнала от других судов / СУДС', en: 'Confirm reception of own signal from other ships / VTS', def: 'Checked, OK' },
          { k: 's6_3', ru: 'Опрос СУДС / береговой станции', en: 'Polling by VTS / shore installation', def: 'Checked, OK' },
        ],
      },
    ],
    extra: [
      { k: 'emi', ru: 'Электромагнитные помехи от АИС другим установкам?', en: 'Electromagnetic interference from AIS observed to other installations?', def: 'No' },
      { k: 'remarks', ru: 'Замечания', en: 'Remarks', def: 'None' },
      { k: 'place', ru: 'Место проведения', en: 'Place', def: '' },
    ],
  },
  {
    key: 'r6322', suffix: 'GMDSS', units_max: 1,
    name: 'Отчёт о проверке судового радиооборудования (форма 6.3.22.2 РС)',
    name_en: 'Report on examination of ship’s radio equipment (RS form 6.3.22.2)',
    header: ШАПКА_СУДНА,
    rows: [
      { k: 'place', ru: 'Место проведения проверки', en: 'Place of examination', def: '' },
      { k: 'surveyor', ru: 'Проверку выполнил', en: 'Examined by', def: '' },
      { k: 'notes', ru: 'Примечания / замечания', en: 'Remarks', def: '' },
    ],
    note: 'Полная форма 6.3.22.2 заполняется в бланке РС; здесь — карточка проверки для базы и предзаполнения (дата, судно, IMO, примечания). Архивные отчёты хранятся с датой и судном.',
  },
]

export const бланкПоТипу = (тип?: string | null): Бланк | undefined =>
  БЛАНКИ.find((б) => б.key === тип)
